import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Clock3,
  Facebook,
  Heart,
  Instagram,
  Mail,
  MapPin,
  Menu,
  Phone,
  X,
  Youtube,
} from "lucide-react";
import { createPageUrl } from "./utils";
import { navigation, serviceSchedule, siteMeta, socialLinks } from "./siteData";
import { useLanguage } from "./i18n";

function getSocialIcon(label) {
  if (label === "Facebook") {
    return Facebook;
  }

  if (label === "YouTube") {
    return Youtube;
  }

  return Instagram;
}

function ChurchLogo({ className = "", imageClassName = "" }) {
  const [imageAvailable, setImageAvailable] = useState(true);

  if (siteMeta.logoPath && imageAvailable) {
    return (
      <img
        src={siteMeta.logoPath}
        alt={`${siteMeta.shortName} logo`}
        className={`${className} ${imageClassName}`.trim()}
        onError={() => setImageAvailable(false)}
      />
    );
  }

  return (
    <div
      className={`${className} gold-gradient flex items-center justify-center text-white font-serif-elegant font-bold`.trim()}
      aria-hidden="true"
    >
      ✝
    </div>
  );
}

function LanguageSwitcher({ language, setLanguage, className = "" }) {
  const optionBase =
    "px-1 text-xs sm:text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227] focus-visible:ring-offset-2";

  return (
    <div
      className={`flex items-center justify-end whitespace-nowrap font-sans-clean ${className}`}
      aria-label="Select site language"
    >
      <button
        type="button"
        onClick={() => setLanguage("en")}
        className={`${optionBase} ${
          language === "en"
            ? "text-[#722F37]"
            : "text-slate-700 hover:text-[#722F37]"
        }`}
        aria-pressed={language === "en"}
      >
        English
      </button>
      <span className="px-1 text-slate-400" aria-hidden="true">
        |
      </span>
      <button
        type="button"
        onClick={() => setLanguage("am")}
        className={`${optionBase} ${
          language === "am"
            ? "text-[#722F37]"
            : "text-slate-700 hover:text-[#722F37]"
        }`}
        aria-pressed={language === "am"}
      >
        አማርኛ
      </button>
    </div>
  );
}

export default function Layout({ children, currentPageName }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMobileMenuOpen(false);
  }, [currentPageName]);

  const localizedServiceSchedule = useMemo(
    () => t("home.serviceSchedule", serviceSchedule),
    [t]
  );
  const currentService = useMemo(
    () => localizedServiceSchedule[0] || serviceSchedule[0],
    [localizedServiceSchedule]
  );
  const isHomePage = currentPageName === "Home";

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f1e8] text-slate-900">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
        
        :root {
          --gold: #C9A227;
          --gold-dark: #A88520;
          --burgundy: #722F37;
          --burgundy-dark: #5A252C;
        }
        
        .font-serif-elegant {
          font-family: 'Cormorant Garamond', Georgia, serif;
        }
        
        .font-sans-clean {
          font-family: 'Inter', system-ui, sans-serif;
        }
        
        .ethiopian-cross {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath fill='%23C9A22710' d='M50 0L55 45L100 50L55 55L50 100L45 55L0 50L45 45Z'/%3E%3C/svg%3E");
          background-size: 60px;
        }
        
        .gold-gradient {
          background: linear-gradient(135deg, var(--gold) 0%, var(--gold-dark) 100%);
        }
        
        .burgundy-gradient {
          background: linear-gradient(135deg, var(--burgundy) 0%, var(--burgundy-dark) 100%);
        }

        .hero-overlay {
          background:
            radial-gradient(circle at top, rgba(201, 162, 39, 0.18), transparent 45%),
            linear-gradient(160deg, rgba(32, 16, 18, 0.82), rgba(114, 47, 55, 0.9));
        }

        .panel-surface {
          background: rgba(255, 255, 255, 0.78);
          backdrop-filter: blur(12px);
        }
      `}</style>

      <div className="bg-[#4b1d23] text-white py-2 px-4 text-sm font-sans-clean hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a
              href={siteMeta.phoneHref}
              className="flex items-center gap-2 hover:text-[#C9A227] transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              <span>{siteMeta.phone}</span>
            </a>
            <a
              href={siteMeta.emailHref}
              className="flex items-center gap-2 hover:text-[#C9A227] transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              <span>{siteMeta.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/80 flex items-center gap-2">
              <Clock3 className="w-3.5 h-3.5" />
              {t("layout.currentServiceLabel")}: {currentService.title} • {currentService.time}
            </span>
            <div className="flex gap-3 ml-4">
              {socialLinks.map((link) => {
                const Icon = getSocialIcon(link.label);

                return (
                  <a
                    key={link.label}
                    href={link.href}
                    aria-label={link.label}
                    className="hover:text-[#C9A227] transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${
          isScrolled || !isHomePage
            ? "bg-white/92 backdrop-blur-md shadow-lg"
            : "bg-white/96 backdrop-blur-md shadow-lg border-b border-[#722F37]/10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center py-4">
            <Link to={createPageUrl("Home")} className="flex items-center gap-3 group">
              <ChurchLogo
                className="w-14 h-14 rounded-full overflow-hidden shadow-lg ring-2 ring-white/30 group-hover:scale-105 transition-transform bg-white"
                imageClassName="object-cover"
              />
              <div className="hidden sm:block">
                <h1
                  className="font-serif-elegant text-xl font-bold text-[#722F37] transition-colors"
                >
                  {siteMeta.shortName}
                </h1>
                <p
                  className="text-xs font-sans-clean text-gray-600 transition-colors"
                >
                  {t("layout.churchInMadison")}
                </p>
              </div>
            </Link>

            <nav className="hidden xl:flex items-center gap-1">
              {navigation.map((link) => (
                <Link
                  key={link.page}
                  to={createPageUrl(link.page)}
                  className={`px-4 py-2 rounded-lg font-sans-clean text-sm font-medium transition-all hover:bg-[#C9A227]/10 ${
                    currentPageName === link.page
                      ? "text-[#C9A227]"
                      : "text-gray-700 hover:text-[#722F37]"
                  }`}
                >
                  {t(`navigation.${link.page}`, link.name)}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <Link
                to={createPageUrl("Donations")}
                className="burgundy-gradient text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
              >
                <Heart className="w-4 h-4" />
                <span className="hidden sm:inline">{t("layout.give")}</span>
              </Link>

              <LanguageSwitcher
                language={language}
                setLanguage={setLanguage}
                className="order-last"
              />

              <button
                type="button"
                onClick={() => setMobileMenuOpen((value) => !value)}
                className="xl:hidden p-2 rounded-lg text-gray-700 transition-colors hover:bg-[#722F37]/10"
                aria-label="Toggle navigation menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="primary-navigation"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="xl:hidden bg-white border-t shadow-xl">
            <nav id="primary-navigation" className="max-w-7xl mx-auto px-4 py-4 space-y-1">
              {navigation.map((link) => (
                <Link
                  key={link.page}
                  to={createPageUrl(link.page)}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg font-sans-clean text-sm font-medium transition-colors ${
                    currentPageName === link.page
                      ? "bg-[#C9A227]/10 text-[#C9A227]"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {t(`navigation.${link.page}`, link.name)}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-[#191516] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <ChurchLogo
                  className="w-11 h-11 rounded-full overflow-hidden bg-white ring-1 ring-white/10"
                  imageClassName="object-cover"
                />
                <div>
                  <h3 className="font-serif-elegant text-lg font-bold text-[#C9A227]">
                    {siteMeta.shortName}
                  </h3>
                  <p className="text-xs text-gray-400">{siteMeta.city}</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm font-sans-clean leading-relaxed">
                {t("layout.mission")}
              </p>
            </div>

            <div>
              <h4 className="font-serif-elegant text-lg font-semibold mb-6 text-[#C9A227]">
                {t("layout.explore")}
              </h4>
              <ul className="space-y-3 font-sans-clean text-sm">
                {navigation.map((link) => (
                  <li key={link.page}>
                    <Link
                      to={createPageUrl(link.page)}
                      className="text-gray-400 hover:text-[#C9A227] transition-colors inline-flex items-center gap-2"
                    >
                      <ChevronRight className="w-4 h-4" />
                      {t(`navigation.${link.page}`, link.name)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-serif-elegant text-lg font-semibold mb-6 text-[#C9A227]">
                {t("layout.serviceRhythm")}
              </h4>
              <ul className="space-y-3 font-sans-clean text-sm text-gray-400">
                {localizedServiceSchedule.map((item) => (
                  <li key={item.title}>
                    <span className="text-white">{item.title}</span>
                    <br />
                    {item.time}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-serif-elegant text-lg font-semibold mb-6 text-[#C9A227]">
                {t("layout.contactUs")}
              </h4>
              <ul className="space-y-4 font-sans-clean text-sm">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400">
                    {siteMeta.addressLine1}
                    <br />
                    {siteMeta.addressLine2}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#C9A227] flex-shrink-0" />
                  <a
                    href={siteMeta.phoneHref}
                    className="text-gray-400 hover:text-[#C9A227] transition-colors"
                  >
                    {siteMeta.phone}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#C9A227] flex-shrink-0" />
                  <a
                    href={siteMeta.emailHref}
                    className="text-gray-400 hover:text-[#C9A227] transition-colors"
                  >
                    {siteMeta.email}
                  </a>
                </li>
              </ul>
              <div className="flex gap-4 mt-6">
                {socialLinks.map((link) => {
                  const Icon = getSocialIcon(link.label);

                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      aria-label={link.label}
                      className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C9A227] transition-colors"
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm font-sans-clean">
              © {new Date().getFullYear()} {siteMeta.name}.{" "}
              {t("layout.allRightsReserved")}
            </p>

            <p className="text-gray-500 text-sm font-sans-clean text-center">
              {t("layout.scripture")}
            </p>
          </div>

          <div className="mt-4 text-center text-sm font-sans-clean text-gray-400">
            {t("layout.builtFor")}
          </div>

          <div className="mt-2 text-center text-xs font-sans-clean text-gray-500">
            {t("layout.poweredBy")}{" "}
            <a
              href="https://www.dewacon.net"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#C9A227] transition-colors"
            >
              dewacon
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
